import { Autocomplete, AutocompleteChangeDetails, AutocompleteChangeReason, Button, Grid, TextField, Tooltip } from '@mui/material';
import CustomAccordion from '../CustomAccordion/CustomAccordion';
import React, { SyntheticEvent, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';
import { resetInvestmentInput, setInvestmentInputExpand, setSelectedInvestmentId, setTriggerFocusCompanyNameInput, setInvestmentInput, setAmount, setItemsAndTypes, setItems, setType, setInterest, setInvestmentDate, setDescription, setSettleDate } from './reducers/InvestmentInputReducer';
import BaseResponse from '../../app/BaseResponse';
import Utility from '../../app/utility';
import { showLoading } from '../LoadingView/reducers/LoadingViewReducer';
import { InvestmentInputSelector } from './reducers/InvestmentInputReducer';
import InvestmentApi from './api/InvestmentApi';
import InvestmentModel from './models/InvestmentModel';
import NumberFormatCustom from '../NumberFormatCustom/NumberFormatCustom';
import ItemsAndTypesModel from './models/ItemsAndTypesModel';
import StartEndDatepicker from '../StartEndDatepicker/StartEndDatepicker';
import { setTriggerInvestmentSearch } from './reducers/InvestmentSearchReducer';

const InvestmentInput: React.FunctionComponent = () => {
    const dispatch = useDispatch();
    const investmentInputViewData = useSelector(InvestmentInputSelector);

    useEffect(() => {
        if (investmentInputViewData.triggerSave > 0) {
            save();
        }
    }, [investmentInputViewData.triggerSave]);

    useEffect(() => {
        dispatch(showLoading(true));
        InvestmentApi.GetInvestmentItemsAndTypes().then((response: BaseResponse<ItemsAndTypesModel>) => {
            if (response.success) {
                dispatch(setItemsAndTypes(response.data));

                dispatch(showLoading(false));

            } else {
                Utility.Alert.Error(response.message);
                dispatch(showLoading(false));
            }
        }).catch((error) => {
            Utility.Alert.Error(error);
            dispatch(showLoading(false));
        })
    }, []);

    useEffect(() => {
        if (investmentInputViewData.triggerRefreshInput > 0) {
            refreshInput();
        }
    }, [investmentInputViewData.triggerRefreshInput]);

    const refreshInput = () => {
        dispatch(resetInvestmentInput());
        dispatch(setSelectedInvestmentId(""));
        dispatch(setTriggerFocusCompanyNameInput((new Date()).getTime()));
    }

    useEffect(() => {
        if (investmentInputViewData.selectedInvestmentId.length > 0) {
            selectInvestmentById(investmentInputViewData.selectedInvestmentId);
        }
    }, [investmentInputViewData.selectedInvestmentId])

    const selectInvestmentById = (id: string) => {
        dispatch(showLoading(true));
        InvestmentApi.GetById({
            id: id
        }).then((response: BaseResponse<InvestmentModel>) => {
            if (response.success) {
                dispatch(setInvestmentInput(response.data));

                dispatch(showLoading(false));

            } else {
                Utility.Alert.Error(response.message);
                dispatch(showLoading(false));
            }
        }).catch((error) => {
            Utility.Alert.Error(error);
            dispatch(showLoading(false));
        })
    };

    const save = () => {
        try {
            validate();

            InvestmentApi.Save(investmentInputViewData.input)
                .then((response: BaseResponse<InvestmentModel>) => {
                    if (response.success) {
                        if (investmentInputViewData.input.id.length === 0) {
                            Utility.Alert.Success("Investment added successful");
                        } else {
                            Utility.Alert.Success("Investment updated successful");
                        }

                        refreshInput();

                        dispatch(setTriggerInvestmentSearch(new Date().getTime()));
                    } else {
                        Utility.Alert.Error(response.message);
                    }
                })
                .catch((error) => {
                    Utility.Alert.Error(error);
                });
        } catch (error) {
            Utility.Alert.Error(error);
        }
    }

    const validate = () => {
        if (investmentInputViewData.input.items.length === 0) {
            throw "Items is required";
        }

        if (investmentInputViewData.input.type.length === 0) {
            throw "Type is required";
        }

        if (!investmentInputViewData.input.amount || investmentInputViewData.input.amount === 0) {
            throw "Amount is required";
        }

        if (investmentInputViewData.input.investmentDate.length === 0) {
            throw "Start date is required";
        }

        if (investmentInputViewData.input.settleDate.length === 0) {
            throw "Settle date is required";
        }

        if (!investmentInputViewData.input.interest || investmentInputViewData.input.interest === 0) {
            throw "Interest is required";
        }
    }

    return (
        <>
            {
                <CustomAccordion
                    title="Input"
                    defaultOpen={true}
                    className="top-quick-input"
                    open={investmentInputViewData.InvestmentInputExpand}
                    onOpenChange={(isOpen: boolean) => {
                        dispatch(setInvestmentInputExpand(isOpen));
                    }}
                >
                    <Grid container>
                        <Grid item lg={3} xs={12}>
                            <Grid container>
                                <Grid xs={10} lg={9}>
                                    <StartEndDatepicker
                                        startDateLabel="Investment Date"
                                        useQuickEndDateInput={false}
                                        className='cargo-input-component'
                                        onStartDateChange={(date: string) => {
                                            if (date) {
                                                dispatch(setInvestmentDate(date));
                                            }
                                        }}
                                        startDateValue={Utility.DateFormat.formatDateString(investmentInputViewData.input.investmentDate).toUpperCase()}
                                        useSingleMode={true}
                                    >
                                    </StartEndDatepicker>
                                </Grid>
                                <Grid item xs={2} lg={3}>
                                    <Tooltip title="Reset (Alt + R)">
                                        <Button
                                            className="clear-input-button"
                                            tabIndex={-1}
                                            color="success"
                                            variant="contained"
                                            onClick={() => {
                                                refreshInput();
                                            }}
                                        >
                                            <i className="fa fa-refresh" aria-hidden="true"></i>
                                        </Button>
                                    </Tooltip>
                                </Grid>
                            </Grid>
                            <Autocomplete
                                className='cargo-input-component'
                                options={["", "Finished"]}
                                renderInput={(params) => <TextField {...params} label="Description" />}
                                value={investmentInputViewData.input.description}
                                onChange={(event: SyntheticEvent<Element, Event>, value: string | null, reason: AutocompleteChangeReason, details?: AutocompleteChangeDetails<string> | undefined) => {
                                    dispatch(setDescription(value ? value : ""));
                                }}
                            />
                        </Grid>
                        <Grid item lg={3} xs={12}>
                            <Autocomplete
                                className='cargo-input-component'
                                freeSolo={true}
                                options={investmentInputViewData.itemsAndTypes.items}
                                renderInput={(params) => <TextField {...params} label="Items" />}
                                value={investmentInputViewData.input.items}
                                onChange={(event: React.SyntheticEvent, value: string | null, reason: string, details?: AutocompleteChangeDetails<string> | undefined) => {
                                    dispatch(setItems(value ? value : ""));
                                }}
                                onInputChange={(event: React.SyntheticEvent, value: string, reason: string) => {
                                    dispatch(setItems(value ? value : ""));
                                }}
                            />
                            <Autocomplete
                                className='cargo-input-component'
                                freeSolo={true}
                                options={investmentInputViewData.itemsAndTypes.types}
                                renderInput={(params) => <TextField {...params} label="Type" />}
                                value={investmentInputViewData.input.type}
                                onChange={(event: React.SyntheticEvent, value: string | null, reason: string, details?: AutocompleteChangeDetails<string> | undefined) => {
                                    dispatch(setType(value ? value : ""));
                                }}
                                onInputChange={(event: React.SyntheticEvent, value: string, reason: string) => {
                                    dispatch(setType(value ? value : ""));
                                }}
                            />


                        </Grid>
                        <Grid item lg={3} xs={12}>
                            <TextField
                                className='cargo-input-component'
                                fullWidth
                                label="Amount"
                                InputProps={{
                                    inputComponent: NumberFormatCustom as any,
                                }}
                                value={investmentInputViewData.input.amount ? investmentInputViewData.input.amount : ""}
                                onChange={(event) => {
                                    let value = event.target.value.length == 0 ? 0 : parseFloat(event.target.value);
                                    dispatch(setAmount(value));
                                }}
                            >
                            </TextField>
                            <StartEndDatepicker
                                startDateLabel="Settle Date"
                                useQuickEndDateInput={false}
                                className='cargo-input-component'
                                onStartDateChange={(date: string) => {
                                    if (date) {
                                        dispatch(setSettleDate(date));
                                    }
                                }}
                                startDateValue={Utility.DateFormat.formatDateString(investmentInputViewData.input.settleDate).toUpperCase()}
                                useSingleMode={true}
                            >
                            </StartEndDatepicker>
                        </Grid>
                        <Grid item lg={2} xs={12}>
                            <TextField
                                className='cargo-input-component'
                                fullWidth
                                label="Interest"
                                InputProps={{
                                    inputComponent: NumberFormatCustom as any,
                                }}
                                value={investmentInputViewData.input.interest ? investmentInputViewData.input.interest : ""}
                                onChange={(event) => {
                                    let value = event.target.value.length == 0 ? 0 : parseFloat(event.target.value);
                                    dispatch(setInterest(value));
                                }}
                            >
                            </TextField>
                        </Grid>
                        <Grid item lg={1} xs={12}>
                            <Tooltip title="Alt + S">
                                <Button
                                    onClick={save}
                                    variant="outlined"
                                >
                                    Save (s)
                                </Button>
                            </Tooltip>
                        </Grid>
                    </Grid>
                </CustomAccordion>
            }
        </>
    )
};

export default InvestmentInput;